/**
 * Class join codes. Distinct from ULIDs and from one-time adult/PIN codes:
 * kids type these, so 0/O and 1/I are left out of the alphabet.
 */

export const JOIN_CODE_ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
export const JOIN_CODE_LENGTH = 8;

/** Strip separators and the lookalikes 0/O/1/I so typed codes match JOIN_CODE. */
export function normalizeJoinCode(raw: string): string {
  let out = "";
  for (const ch of raw.toUpperCase()) {
    if (!JOIN_CODE_ALPHABET.includes(ch)) continue;
    out += ch;
    if (out.length === JOIN_CODE_LENGTH) break;
  }
  return out;
}

export function joinCodeFromBytes(bytes: Uint8Array): string {
  if (bytes.length < JOIN_CODE_LENGTH) {
    throw new Error("join code needs 8 random bytes");
  }
  let out = "";
  for (let i = 0; i < JOIN_CODE_LENGTH; i++) {
    out += JOIN_CODE_ALPHABET[bytes[i]! % JOIN_CODE_ALPHABET.length];
  }
  return out;
}

/**
 * One-time codes (recovery, PIN reset, adult and co-lead invites) are 12
 * characters from the same alphabet, shown as XXXX-XXXX-XXXX. The worker
 * ignores dashes, spaces and case (`hashOneTimeCode`).
 */
export const ONE_TIME_CODE_LENGTH = 12;

const SEPARATORS = /[-\s]/g;

/**
 * A one-time code as it is typed (issue #123): capitals, dashes after 4 and
 * 8 characters, at most 12 characters. Characters no code holds stay in, so
 * the field can say so rather than swallow them (`codeStrays`).
 */
export function formatOneTimeCode(raw: string): string {
  const chars = raw.toUpperCase().replace(SEPARATORS, "").slice(0, ONE_TIME_CODE_LENGTH);
  return chars.replace(/(.{4})(?=.)/g, "$1-");
}

/**
 * The one-time code in a pasted text such as «Code: PB8L-6NFB-ZZJJ» from the
 * saved recovery file, formatted; null if there is none. Grouped with dashes
 * first, then twelve characters in one piece, then grouped with spaces.
 */
export function findOneTimeCode(text: string): string | null {
  const upper = text.toUpperCase();
  const found =
    upper.match(/\b[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}\b/) ??
    upper.match(/\b[A-Z0-9]{12}\b/) ??
    upper.match(/\b[A-Z0-9]{4} [A-Z0-9]{4} [A-Z0-9]{4}\b/);
  return found ? formatOneTimeCode(found[0]) : null;
}

/** The characters of a typed code that no code holds, each once. */
export function codeStrays(code: string): string {
  let out = "";
  for (const ch of code.replace(SEPARATORS, "")) {
    if (!JOIN_CODE_ALPHABET.includes(ch) && !out.includes(ch)) out += ch;
  }
  return out;
}

/**
 * Name and code from the file «Schreib das auf» saves (`vocifuchs-code.txt`,
 * issue #125): a `Name:` line and a `Code:` line. Either is null if missing.
 * Without a `Code:` line only a code with its dashes counts, so words in some
 * other file are not taken for a code.
 */
export function readRecoveryFile(text: string): { name: string | null; code: string | null } {
  const name = text.match(/^[ \t\uFEFF]*Name:[ \t]*(\S.*?)[ \t\r]*$/m)?.[1] ?? null;
  const codeLine = text.match(/^[ \t\uFEFF]*Code:(.*)$/m)?.[1];
  const code =
    codeLine !== undefined
      ? findOneTimeCode(codeLine)
      : (text.match(/\b[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}\b/i)?.[0] ?? null);
  return { name, code: code === null ? null : formatOneTimeCode(code) };
}
